import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { ICities } from '../../../../../@interfaces/cities.interface';
import { ITypes } from '../../../../../@interfaces/types.interface';
import { TributaryService } from '../../../../../@pages/tributary/tributary.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { Router } from '@angular/router';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { AlertService } from '@shared-services';

@Component({
  selector: 'app-register-ica-bases',
  templateUrl: './register-ica-bases.component.html',
  styleUrls: ['./register-ica-bases.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
  ],
})
export class RegisterIcaBasesComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private tributaryService = inject(TributaryService);
  private translate = inject(TranslateService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);

  icaBaseForm: UntypedFormGroup = this.fb.group({
    city: ['', [Validators.required]],
    id_type: ['', [Validators.required]],
    uvt: ['', [Validators.required]],
  });
  citiesList: ICities[] = [];
  typesList: ITypes[] = [];
  companyObject = {} as IStorageCompany;

  ngOnInit(): void {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    const country_id = this.companyObject.countries[0].id_country;
    this.tributaryService
      .getCitiesByCompany(parseInt(country_id!.toString()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.citiesList = result;
      });
    this.tributaryService
      .getAllTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.typesList = result || [];
      });
  }

  textValid(text: string) {
    return (
      this.icaBaseForm.get(text)?.invalid && this.icaBaseForm.get(text)?.touched
    );
  }

  register() {
    this.icaBaseForm.markAllAsTouched();
    if (this.icaBaseForm.invalid === true) {
      this.openAlert(
        'error',
        'tributary.common.error',
        'tributary.icaBases.messages.requiredFields',
      );
      return;
    }
    this.spinnerService.show();
    this.saveOnDataBase();
  }

  saveOnDataBase() {
    this.tributaryService
      .createIcaBase(
        parseInt(this.icaBaseForm.value.city.id_city),
        Number(this.icaBaseForm.value.id_type),
        Number(this.icaBaseForm.value.uvt),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.spinnerService.hide();
          if (!result) {
            this.openAlert(
              'error',
              'tributary.common.error',
              'tributary.icaBases.messages.genericError',
            );
            return;
          }
          this.alertService
            .open({
              title: this.translate.instant('tributary.common.success'),
              message: this.translate.instant(
                'tributary.icaBases.messages.created',
              ),
              showClose: true,
              type: 'success',
            })
            .subscribe({
              accept: () => this.refreshTributary(),
              close: () => this.refreshTributary(),
            });
        },
        error: (error) => {
          this.spinnerService.hide();
          const message =
            error?.graphQLErrors?.[0]?.message || error?.message || '';
          const isDuplicate = message.includes(
            'ICA base already exists for this city and type',
          );
          this.openAlert(
            isDuplicate ? 'warning' : 'error',
            isDuplicate
              ? 'tributary.icaBases.titles.notCreated'
              : 'tributary.common.error',
            isDuplicate
              ? 'tributary.icaBases.messages.duplicate'
              : 'tributary.icaBases.messages.genericError',
          );
        },
      });
  }

  private openAlert(
    type: 'success' | 'error' | 'warning',
    titleKey: string,
    messageKey: string,
  ): void {
    this.alertService.open({
      title: this.translate.instant(titleKey),
      message: this.translate.instant(messageKey),
      showClose: true,
      type,
    });
  }

  private refreshTributary(): void {
    this.router
      .navigateByUrl('/table', { skipLocationChange: true })
      .then(() => {
        this.router.navigate(['/tributary']);
      });
  }
}
